import { hasCurrencyPrecision, parseCurrencyAmount } from "../../../utils/currencyFormat";

export const ids = values => [...new Set((Array.isArray(values) ? values : []).map(String).filter(Boolean))];
export const splitRecipients = value => String(value || "").split(/[;,\n]/).map(item => item.trim()).filter(Boolean);
export function makeDraft(raw = {}, interventionId = null) {
  return {
    packetId: raw.packetId || null, version: raw.version || null,
    interventionId: raw.interventionId || interventionId || null,
    to: Array.isArray(raw.to) ? raw.to : [], cc: Array.isArray(raw.cc) ? raw.cc : [],
    subject: raw.subject || "", notes: raw.notes || "",
    lines: Array.isArray(raw.lines) ? raw.lines.map((line, index) => ({ ...line, clientId: line.clientId || `existing-${line.id || index}` })) : [],
    attachmentDocumentIds: ids(raw.attachmentDocumentIds),
    excludedAutomaticDocumentIds: ids(raw.excludedAutomaticDocumentIds),
    evidenceSelections: raw.evidenceSelections && typeof raw.evidenceSelections === "object" ? raw.evidenceSelections : {},
    evidenceConfirmations: raw.evidenceConfirmations && typeof raw.evidenceConfirmations === "object" && !Array.isArray(raw.evidenceConfirmations) ? raw.evidenceConfirmations : {},
  };
}
export function lineKey(line) { return String(line.clientId || line.id); }
export function remainingForSupport(support, lines, excludingLineKey = null) {
  if (!support) return null;
  const approved = Number(support.approvedAmount);
  const other = Number(support.otherRequestsAmount);
  if (!Number.isFinite(approved) || !Number.isFinite(other)) return null;
  const inDraft = (lines || []).filter(line => String(line.supportId) === String(support.id) && lineKey(line) !== excludingLineKey).reduce((sum, line) => sum + Math.round(Number(line.amount || 0) * 100), 0);
  return (Math.round(approved * 100) - Math.round(other * 100) - inDraft) / 100;
}
// Approved support schedules use calendar months, including partial first/last
// months. Work in UTC so date-only input does not shift with the browser zone.
const calendarDate = value => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return null;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value ? parsed : null;
};
const monthlyPeriods = support => {
  let cursor = calendarDate(support.startDate);
  const end = calendarDate(support.endDate);
  const count = Number(support.occurrences);
  const hasCount = Number.isInteger(count) && count > 0;
  if (!cursor || (support.endDate && !end) || (!end && !hasCount) || (end && cursor > end)) return [];
  const periods = [];
  for (let index = 0; index < Math.min(hasCount ? count : 120, 120); index += 1) {
    let last = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0));
    if (end && last > end) last = end;
    periods.push({ servicePeriodStart: cursor.toISOString().slice(0, 10), servicePeriodEnd: last.toISOString().slice(0, 10) });
    cursor = new Date(Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), last.getUTCDate() + 1));
    if (end && cursor > end) break;
  }
  return periods;
};
export function suggestPaymentValues(support, lines = [], excludingLineKey = null) {
  const values = { amount: "", servicePeriodStart: "", servicePeriodEnd: "", requestedPaymentDate: "" };
  const remaining = remainingForSupport(support, lines, excludingLineKey);
  if (!support || remaining === null || remaining <= 0) return values;
  const recurring = support.recurrenceMode !== "not_allowed" && (support.recurrenceMode === "required" || support.recurrenceEnabled);
  if (!recurring) return { ...values, amount: String(remaining) };
  const periods = monthlyPeriods(support);
  const saved = support.existingPeriods || [];
  const draft = lines.filter(line => String(line.supportId) === String(support.id) && lineKey(line) !== excludingLineKey);
  const history = [...saved, ...draft];
  const unknownHistory = (Number(support.otherRequestsAmount) > 0 && !saved.length) || history.some(line =>
    !calendarDate(line.servicePeriodStart) || !calendarDate(line.servicePeriodEnd) || line.servicePeriodEnd < line.servicePeriodStart);
  let nextIndex = -1;
  if (!unknownHistory) {
    nextIndex = periods.findIndex(period => {
      const overlaps = history.filter(line => line.servicePeriodStart <= period.servicePeriodEnd && line.servicePeriodEnd >= period.servicePeriodStart);
      // A partial/custom period needs a human decision; do not skip an unpaid gap.
      return !overlaps.length || !overlaps.some(line => line.servicePeriodStart <= period.servicePeriodStart && line.servicePeriodEnd >= period.servicePeriodEnd);
    });
    if (nextIndex >= 0) {
      const period = periods[nextIndex];
      if (history.some(line => line.servicePeriodStart <= period.servicePeriodEnd && line.servicePeriodEnd >= period.servicePeriodStart)) nextIndex = -1;
      else Object.assign(values, period, { requestedPaymentDate: period.servicePeriodEnd });
    } else if (periods.length) return values; // All approved periods are already covered.
  }
  let installment = Number(support.amountPerPeriod);
  if (!(installment > 0) && nextIndex >= 0) {
    const totalCents = Math.round(Number(support.approvedAmount) * 100);
    const baseCents = Math.floor(totalCents / periods.length);
    // Match the existing schedule split: any rounding remainder goes to the last period.
    installment = (baseCents + (nextIndex === periods.length - 1 ? totalCents - baseCents * periods.length : 0)) / 100;
  }
  if (Number.isFinite(installment) && installment > 0) values.amount = String(Math.min(Math.round(installment * 100), Math.round(remaining * 100)) / 100);
  return values;
}
export function validatePaymentEditor(editor, support, lines) {
  const errors = {};
  const amount = parseCurrencyAmount(editor?.amount);
  if (!support) errors.supportId = "Choose an approved funding line.";
  if (!Number.isFinite(amount) || amount <= 0 || !hasCurrencyPrecision(editor?.amount)) errors.amount = "Enter an amount greater than $0 with up to two decimal places.";
  const remaining = remainingForSupport(support, lines, editor?.clientId || null);
  if (Number.isFinite(amount) && remaining !== null && amount > remaining) errors.amount = "This amount exceeds the remaining approved funding.";
  const start = editor?.servicePeriodStart || "";
  const end = editor?.servicePeriodEnd || "";
  if (support?.recurrenceMode === "required" && (!start || !end)) errors.period = "Enter the payment period.";
  if ((start && !end) || (!start && end)) errors.period = "Enter both dates for the payment period.";
  if (start && end && end < start) errors.period = "The end date must be on or after the start date.";
  return errors;
}
export function attachDocument(draft, documentId, requirement, confirmation = null) {
  const id = String(documentId);
  const needsAttachment = !requirement || requirement.location === "attachment";
  const confirmations = { ...draft.evidenceConfirmations };
  const selections = { ...draft.evidenceSelections };
  const previousIds = requirement && needsAttachment ? ids(selections[requirement.key]) : [];
  if (requirement) {
    selections[requirement.key] = [id];
    delete confirmations[requirement.key];
    if (confirmation) confirmations[requirement.key] = { ...confirmation, documentId: id, confirmed: true };
  }
  const stillSelected = new Set(Object.values(selections).flat().map(String));
  const attachments = draft.attachmentDocumentIds.filter(value => !previousIds.includes(String(value)) || stillSelected.has(String(value)));
  return {
    ...draft,
    attachmentDocumentIds: needsAttachment ? ids([...attachments, id]) : attachments,
    excludedAutomaticDocumentIds: draft.excludedAutomaticDocumentIds.filter(value => String(value) !== id),
    evidenceSelections: selections,
    evidenceConfirmations: confirmations,
  };
}
export function removeAttachment(draft, documentId, requirements = []) {
  const id = String(documentId);
  const selections = { ...draft.evidenceSelections };
  const confirmations = { ...draft.evidenceConfirmations };
  requirements.filter(item => item.location === "attachment").forEach(item => {
    if (selections[item.key]) selections[item.key] = selections[item.key].filter(value => String(value) !== id);
    if (String(confirmations[item.key]?.documentId) === id) delete confirmations[item.key];
  });
  return { ...draft, attachmentDocumentIds: draft.attachmentDocumentIds.filter(value => String(value) !== id), excludedAutomaticDocumentIds: ids([...draft.excludedAutomaticDocumentIds, id]), evidenceSelections: selections, evidenceConfirmations: confirmations };
}
export function selectIntervention(draft, interventionId) {
  // Explicitly added documents are independent of any payment line.
  return { ...draft, interventionId: interventionId || null, lines: [], evidenceSelections: {}, evidenceConfirmations: {}, excludedAutomaticDocumentIds: [] };
}
